import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

// material-ui
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CircularProgress from '@mui/material/CircularProgress';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import MyLocationIcon from '@mui/icons-material/MyLocation';

// project imports
import useGeoLocation from '../../services/geoLocation';

// ==============================|| GEO LOCATION GUARD ||============================== //

/**
 * GeoLocationGuard wraps the entire app.
 * - On mount it immediately requests geolocation.
 * - While waiting → shows a non-dismissible dialog with a loading spinner.
 * - If granted   → dialog closes, children render normally.
 * - If denied    → shows a non-dismissible dialog; user MUST enable location to proceed.
 */
export default function GeoLocationGuard({ children }) {
  const { locationStatus, errorMessage, fetchGeoLocation } = useGeoLocation();
  const [retrying, setRetrying] = useState(false);

  // Request location on first render
  useEffect(() => {
    fetchGeoLocation().catch(() => {
      // error state is handled via locationStatus
    });
  }, []);

  // ---- Handle "Retry" click ----
  const handleRetry = async () => {
    setRetrying(true);
    try {
      await fetchGeoLocation();
    } catch {
      // still denied – state already updated inside the hook
    } finally {
      setRetrying(false);
    }
  };

  // Only show dialog when location is denied — pending state is silent
  const isOpen = locationStatus === 'denied';

  return (
    <>
      {children}

      {/* Non-dismissible dialog – only shown when location is denied */}
      <Dialog
        open={isOpen}
        disableEscapeKeyDown
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            maxWidth: 440,
            width: '100%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
          }
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(4px)'
            }
          }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
          <Stack spacing={2} alignItems="center">
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: '#fdecea',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <LocationOffIcon sx={{ fontSize: 36, color: '#e74c3c' }} />
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 700, color: '#333' }}>
              Location Access Required
            </Typography>

            <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.6 }}>
              {errorMessage ||
                'Location permission is required to use this portal. Please enable location access in your browser settings and try again.'}
            </Typography>

            <Box
              sx={{
                bgcolor: '#fff8e1',
                border: '1px solid #ffe082',
                borderRadius: 2,
                p: 1.5,
                width: '100%'
              }}
            >
              <Typography variant="caption" sx={{ color: '#856404', fontWeight: 600, display: 'block', mb: 0.5 }}>
                How to enable location:
              </Typography>
              <Typography variant="caption" sx={{ color: '#856404' }}>
                Click the <strong>lock / info icon</strong> in your browser's address bar → Set{' '}
                <strong>Location</strong> to <strong>Allow</strong> → Then click Retry.
              </Typography>
            </Box>

            <Button
              variant="contained"
              disabled={retrying}
              onClick={handleRetry}
              startIcon={retrying ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <MyLocationIcon />}
              sx={{
                mt: 1,
                px: 4,
                py: 1,
                background: '#00ABBF',
                '&:hover': { background: '#008C99' },
                fontWeight: 600,
                fontSize: '14px',
                textTransform: 'none',
                borderRadius: 2,
                width: '100%'
              }}
            >
              {retrying ? 'Checking…' : 'Retry Location Access'}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}

GeoLocationGuard.propTypes = { children: PropTypes.any };
